import React, { createContext, useContext, useState, useCallback, useMemo, useEffect, useRef } from "react";
import { ConfigurationContext } from "@contexts/Configuration";
import { PAGE } from "./PageNavigationContext";
import { CONSTANT } from "../constant";

const PageConfigurationContext = createContext();

export const PageConfigurationProvider = ({ children }) => {
  const { appliedConfiguration } = useContext(ConfigurationContext);
  const [selectedChannel, setSelectedChannel] = useState(null);
  const [selectedBenefitTypeId, setSelectedBenefitTypeId] = useState(null);
  const pageConfigCache = useRef({});

  const defaultBenefitTypeId = useMemo(() => {
    if (!appliedConfiguration?.generalSetting?.benefitType) return 1; // Default to Medical
    const benefitType = appliedConfiguration.generalSetting.benefitType.toLowerCase();
    return benefitType === "pharmacy" ? 2 : 1;
  }, [appliedConfiguration?.generalSetting?.benefitType]);

  useEffect(() => {
    if (appliedConfiguration && defaultBenefitTypeId && !selectedBenefitTypeId) {
      setSelectedBenefitTypeId(defaultBenefitTypeId);
    }
  }, [appliedConfiguration, defaultBenefitTypeId, selectedBenefitTypeId]);

  useEffect(() => {
    pageConfigCache.current = {};
  }, [appliedConfiguration, selectedChannel, selectedBenefitTypeId]);

  const resetSelectedChannel = useCallback(
    (pageType) => {
      if (pageType === PAGE.CHANNEL_SELECTION) {
        setSelectedChannel(null);
      }
    },
    [setSelectedChannel]
  );

  const findProjectChannel = useCallback(
    (id) => appliedConfiguration?.projectChannels.find((channel) => channel.projectChannelId === id),
    [appliedConfiguration]
  );

  const getCustomChannelPages = useCallback(
    (pageTypeId) =>
      appliedConfiguration?.customChannelPages?.filter(
        (p) =>
          p.benefitTypeId === selectedBenefitTypeId &&
          p.projectChannelId === selectedChannel &&
          p.pageTypeId === pageTypeId
      ),
    [appliedConfiguration, selectedBenefitTypeId, selectedChannel]
  );

  const htmlCardConfig = useMemo(() => {
    if (!selectedChannel || !selectedBenefitTypeId) return null;
    return {
      channel: findProjectChannel(selectedChannel),
      benefitTypeId: selectedBenefitTypeId,
      clientId: appliedConfiguration?.clientId,
      generalSetting: appliedConfiguration?.generalSetting,
      htmlCardConfig: getCustomChannelPages(PAGE.CHANNEL_HTML_CARD)[0],
    };
  }, [selectedChannel, selectedBenefitTypeId, appliedConfiguration, findProjectChannel, getCustomChannelPages]);

  const searchPageConfig = useMemo(() => {
    if (!selectedChannel || !selectedBenefitTypeId) return null;
    return {
      clientId: appliedConfiguration?.clientId,
      projectId: appliedConfiguration?.projectId,
      productId: appliedConfiguration?.productId,
      indicationId: appliedConfiguration?.indicationId,
      benefitTypeId: selectedBenefitTypeId,
      channel: findProjectChannel(selectedChannel),
      generalSetting: appliedConfiguration?.generalSetting,
      searchPageConfigs: getCustomChannelPages(PAGE.CHANNEL_SEARCH),
      searchDetails: appliedConfiguration?.channelSearchDetails?.find(
        (p) => p.benefitTypeId === selectedBenefitTypeId && p.projectChannelId === selectedChannel
      ),
      coverageCardFields: appliedConfiguration?.coverageCardDataFields?.find(
        (p) => p.benefitTypeId === selectedBenefitTypeId && p.projectChannelId === selectedChannel
      ),
      coverageCardValues: appliedConfiguration?.coverageCardValues?.filter(
        (p) => p.benefitTypeId === selectedBenefitTypeId && p.projectChannelId === selectedChannel
      ),
    };
  }, [selectedChannel, selectedBenefitTypeId, appliedConfiguration, findProjectChannel, getCustomChannelPages]);

  const channelSelectionConfig = useMemo(() => {
    return {
      clientId: appliedConfiguration?.clientId,
      benefitTypeId: selectedBenefitTypeId || defaultBenefitTypeId,
      generalSetting: appliedConfiguration?.generalSetting,
      channels: appliedConfiguration?.projectChannels,
      channelSelectionPage: appliedConfiguration?.channelSelectionPages?.find(
        (p) => p.benefitTypeId === (selectedBenefitTypeId || defaultBenefitTypeId)
      ),
    };
  }, [appliedConfiguration, selectedBenefitTypeId, defaultBenefitTypeId]);

  const getPageConfig = useCallback(
    (pageType) => {
      if (pageConfigCache.current[pageType]) {
        return pageConfigCache.current[pageType];
      }

      let config = null;
      switch (pageType) {
        case PAGE.CHANNEL_HTML_CARD:
          config = htmlCardConfig;
          break;
        case PAGE.CHANNEL_SEARCH:
          config = searchPageConfig;
          break;
        case PAGE.CHANNEL_SELECTION:
          config = channelSelectionConfig;
          break;
        default:
          config = null;
      }

      if (config) pageConfigCache.current[pageType] = config;
      return config;
    },
    [htmlCardConfig, searchPageConfig, channelSelectionConfig]
  );

  const getMobileBreakpoint = useCallback(
    () => {
      if (!appliedConfiguration?.generalSetting?.mobileBreakpoint) {
        return CONSTANT.MATCHES_MOBILE_WIDTH;
      }

      const mbp = CONSTANT.MOBILE_WIDTH_TEMPLATE.replace("[mobileBreakpoint]", appliedConfiguration?.generalSetting?.mobileBreakpoint);
      return mbp;
    },
    [appliedConfiguration]
  );

  const value = {
    selectedChannel,
    setSelectedChannel,
    selectedBenefitTypeId,
    getPageConfig,
    resetSelectedChannel,
    getMobileBreakpoint,
  };

  return <PageConfigurationContext.Provider value={value}>{children}</PageConfigurationContext.Provider>;
};

export const usePageConfiguration = () => {
  const context = useContext(PageConfigurationContext);
  if (!context) {
    throw new Error("usePageConfiguration must be used within a PageConfigurationProvider");
  }
  return context;
};

export default PageConfigurationContext;
